import { useCallback } from 'react';
import { StencilProvider, ThemeMode } from '@amzn/stencil-react-components/context';
import { Col, View } from '@amzn/stencil-react-components/layout';
import { Text, TextVariant } from '@amzn/stencil-react-components/text';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { WithFlyout, FlyoutContent } from '@amzn/stencil-react-components/flyout';
import {
  useFeatureFlagContext,
  FEATURE_FLAGS,
  featureFlagDescriptions,
} from 'src/context/FeatureFlagsContext';
import { FeatureFlagToggles } from 'src/v2/shared/feature-flags';
import { CURRENT_STAGE, Stage } from 'src/contstant/apiEndpoints';

/**
 * Floating dev panel that renders feature flag toggles inside a Stencil Flyout.
 * Wraps its own StencilProvider so design tokens resolve independently of
 * the V1 Cloudscape shell.
 *
 * Only renders in non-production environments.
 */
export function DevPanelFab(): JSX.Element | null {
  const ctx = useFeatureFlagContext();

  const handleReset = useCallback(() => {
    ctx.clearLocalOverrides();
  }, [ctx]);

  if (CURRENT_STAGE === Stage.PROD) return null;

  return (
    <StencilProvider themeMode={ThemeMode.Light}>
      <View
        style={{
          position: 'fixed',
          top: 8,
          left: '15%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
        }}
      >
        <WithFlyout
          renderFlyout={({ close }) => (
            <FlyoutContent
              titleText="Feature Flags"
              onCloseButtonClick={close}
              closeButtonAltText="Close feature flags panel"
              buttons={[
                <Button
                  key="reset"
                  variant={ButtonVariant.Tertiary}
                  onClick={handleReset}
                >
                  Reset All
                </Button>,
              ]}
            >
              <Col gridGap="dimensions.spacing.300">
                <Text variant={TextVariant.BodySM} color="color.text.secondary">
                  Overrides persist in localStorage. Toggle off to revert to environment
                  default.
                </Text>
                <FeatureFlagToggles
                  context={ctx}
                  flags={FEATURE_FLAGS}
                  descriptions={featureFlagDescriptions}
                  heading=""
                />
              </Col>
            </FlyoutContent>
          )}
        >
          {({ open }) => (
            <>
              <Button
                variant={ButtonVariant.Primary}
                onClick={open}
                aria-label="Toggle developer feature flags panel"
                data-testid="dev-panel-fab"
                style={{ backgroundColor: '#ff8400' }}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                  </svg>
                }
              />
              <span hidden id="dev-panel-dialog-hint">
                has pop-up dialog
              </span>
            </>
          )}
        </WithFlyout>
      </View>
    </StencilProvider>
  );
}
